import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/providers/AuthProvider";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";
import {
	COLUMN_COLOR_KEYS,
	COLUMN_COLOR_PALETTE,
	KanbanColumnDef,
	fetchKanbanColumns,
	getMatchStatusLabel,
	makeCustomColumnId,
	repairMatchColumns,
	upsertKanbanColumns
} from "@/integrations/supabase/kanbanColumnsApi";
import { kanbanColumnsQueryKey } from "@/hooks/useKanbanColumns";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { AlertTriangle, Eye, EyeOff, GripVertical, Loader2, Lock, Plus, Trash2 } from "lucide-react";

export default function KanbanColumnsTab() {
	const { currentOrganization } = useAuth();
	const organizationId = currentOrganization?.id;
	const { t, i18n } = useTranslation();
	const { toast } = useToast();
	const queryClient = useQueryClient();

	const [columns, setColumns] = useState<KanbanColumnDef[]>([]);
	const [originalColumns, setOriginalColumns] = useState<KanbanColumnDef[]>([]);
	const [fetching, setFetching] = useState(true);
	const [saving, setSaving] = useState(false);
	const [newLabel, setNewLabel] = useState("");
	const [newColor, setNewColor] = useState("blue");
	const [deleteTarget, setDeleteTarget] = useState<{ column: KanbanColumnDef; matchCount: number } | null>(null);
	const [reassignTo, setReassignTo] = useState("");
	const [deleting, setDeleting] = useState(false);

	// Drag-to-reorder state (same native HTML5 pattern as the sidebar navigation)
	const [dragIndex, setDragIndex] = useState<number | null>(null);
	const [dropIndex, setDropIndex] = useState<number | null>(null);
	const dragNodeRef = useRef<HTMLDivElement | null>(null);

	useEffect(() => {
		if (!organizationId) return;
		setFetching(true);
		fetchKanbanColumns(organizationId)
			.then((config) => {
				const repaired = repairMatchColumns(config.matches);
				setColumns(repaired);
				setOriginalColumns(repaired);
			})
			.catch(() => toast({ title: t("admin.kanbanColumns.failedToLoad"), variant: "destructive" }))
			.finally(() => setFetching(false));
	}, [organizationId]);

	const hasUnsavedChanges = JSON.stringify(columns) !== JSON.stringify(originalColumns);
	const visibleCount = useMemo(() => columns.filter((c) => !c.hidden).length, [columns]);

	const labelFor = (column: KanbanColumnDef) =>
		getMatchStatusLabel(t, column.id, columns, i18n.language);

	const updateColumn = (id: string, patch: Partial<KanbanColumnDef>) =>
		setColumns((prev) => prev.map((c) => (c.id === id ? { ...c, ...patch } : c)));

	const handleDragStart = (e: React.DragEvent<HTMLDivElement>, index: number) => {
		setDragIndex(index);
		dragNodeRef.current = e.currentTarget;
		e.dataTransfer.effectAllowed = "move";
		requestAnimationFrame(() => {
			if (dragNodeRef.current)
				dragNodeRef.current.style.opacity = "0.4";
		});
	};

	const handleDragOver = (e: React.DragEvent<HTMLDivElement>, index: number) => {
		e.preventDefault();
		e.dataTransfer.dropEffect = "move";
		if (dragIndex === null || index === dragIndex) {
			setDropIndex(null);
			return;
		}
		setDropIndex(index);
	};

	const handleDrop = (e: React.DragEvent<HTMLDivElement>, targetIndex: number) => {
		e.preventDefault();
		if (dragIndex === null || dragIndex === targetIndex) return;
		setColumns((prev) => {
			const next = [...prev];
			const [moved] = next.splice(dragIndex, 1);
			next.splice(targetIndex, 0, moved);
			return next;
		});
		setDragIndex(null);
		setDropIndex(null);
	};

	const handleDragEnd = () => {
		if (dragNodeRef.current)
			dragNodeRef.current.style.opacity = "1";
		setDragIndex(null);
		setDropIndex(null);
		dragNodeRef.current = null;
	};

	const handleAddColumn = () => {
		const label = newLabel.trim();
		if (!label) return;
		const id = makeCustomColumnId(label, columns.map((c) => c.id));
		const newColumn: KanbanColumnDef = { id, label, color: newColor, isSystem: false };
		setColumns((prev) => {
			// Insert before the synthetic "refused" column when it is last
			const refusedIndex = prev.findIndex((c) => c.id === "refused");
			const insertAt = refusedIndex === -1 ? prev.length : refusedIndex;
			const next = [...prev];
			next.splice(insertAt, 0, newColumn);
			return next;
		});
		setNewLabel("");
	};

	const requestDelete = async (column: KanbanColumnDef) => {
		const { count, error } = await supabase
			.from("matches")
			.select("id", { count: "exact", head: true })
			.eq("status", column.id);
		if (error) {
			toast({ title: t("admin.kanbanColumns.failedToLoad"), description: error.message, variant: "destructive" });
			return;
		}
		setReassignTo("");
		setDeleteTarget({ column, matchCount: count ?? 0 });
	};

	const confirmDelete = async () => {
		if (!deleteTarget) return;
		const { column, matchCount } = deleteTarget;
		setDeleting(true);
		try {
			if (matchCount > 0) {
				if (!reassignTo) return;
				const { error } = await supabase
					.from("matches")
					.update({ status: reassignTo })
					.eq("status", column.id);
				if (error) throw error;
			}
			setColumns((prev) => prev.filter((c) => c.id !== column.id));
			setDeleteTarget(null);
		} catch (e) {
			toast({ title: t("admin.kanbanColumns.failedToSave"), description: (e as Error).message, variant: "destructive" });
		} finally {
			setDeleting(false);
		}
	};

	const handleSave = async () => {
		if (!organizationId) return;
		setSaving(true);
		try {
			await upsertKanbanColumns(organizationId, { matches: columns });
			setOriginalColumns(columns);
			queryClient.invalidateQueries({ queryKey: kanbanColumnsQueryKey(organizationId) });
			toast({ title: t("admin.kanbanColumns.saved") });
		} catch (e) {
			toast({ title: t("admin.kanbanColumns.failedToSave"), description: (e as Error).message, variant: "destructive" });
		} finally {
			setSaving(false);
		}
	};

	const reassignOptions = useMemo(() => {
		if (!deleteTarget) return [];
		return columns
			.filter((c) => c.id !== deleteTarget.column.id && c.id !== "refused")
			.map((c) => ({ value: c.id, label: labelFor(c) }));
	}, [deleteTarget, columns]);

	const colorSelect = (column: KanbanColumnDef) => (
		<Select
			value={column.color ?? "none"}
			onValueChange={(value) => updateColumn(column.id, { color: value === "none" ? undefined : value })}
		>
			<SelectTrigger className="h-8 w-full">
				<SelectValue />
			</SelectTrigger>
			<SelectContent>
				<SelectItem value="none">{t("admin.kanbanColumns.colors.none")}</SelectItem>
				{COLUMN_COLOR_KEYS.map((key) => (
					<SelectItem key={key} value={key}>
						<span className="flex items-center gap-2">
							<span className={`h-3 w-3 rounded-full ${COLUMN_COLOR_PALETTE[key].dotColor}`} />
							{t(`admin.kanbanColumns.colors.${key}`)}
						</span>
					</SelectItem>
				))}
			</SelectContent>
		</Select>
	);

	if (fetching)
		return (
			<div className="flex justify-center py-8">
				<Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
			</div>
		);

	return (
		<div className="space-y-4">
			{hasUnsavedChanges && (
				<Alert variant="default" className="border-amber-300 bg-amber-50 dark:bg-amber-900/20">
					<AlertTriangle className="h-4 w-4" />
					<AlertDescription>{t("admin.kanbanColumns.unsavedChanges")}</AlertDescription>
				</Alert>
			)}

			<Card className="p-4">
				<div className="flex flex-wrap items-end gap-2 mb-6">
					<div className="flex-1 min-w-[200px] max-w-sm">
						<label className="text-sm font-medium block mb-1">{t("admin.kanbanColumns.label")}</label>
						<Input
							value={newLabel}
							onChange={(e) => setNewLabel(e.target.value)}
							placeholder={t("admin.kanbanColumns.labelPlaceholder")}
							onKeyDown={(e) => {
								if (e.key === "Enter") handleAddColumn();
							}}
						/>
					</div>
					<div>
						<label className="text-sm font-medium block mb-1">{t("admin.kanbanColumns.color")}</label>
						<Select value={newColor} onValueChange={setNewColor}>
							<SelectTrigger className="w-[140px]">
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								{COLUMN_COLOR_KEYS.map((key) => (
									<SelectItem key={key} value={key}>
										<span className="flex items-center gap-2">
											<span className={`h-3 w-3 rounded-full ${COLUMN_COLOR_PALETTE[key].dotColor}`} />
											{t(`admin.kanbanColumns.colors.${key}`)}
										</span>
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>
					<Button onClick={handleAddColumn} disabled={!newLabel.trim()}>
						<Plus className="mr-2 h-4 w-4" />
						{t("admin.kanbanColumns.addColumn")}
					</Button>
				</div>

				{/* Horizontal board preview: drag cards to reorder */}
				<div className="flex gap-3 overflow-x-auto pb-3">
					{columns.map((column, index) => {
						const colors = column.color ? COLUMN_COLOR_PALETTE[column.color] : undefined;
						const isDropTarget = dropIndex === index && dragIndex !== null;
						return (
							<div
								key={column.id}
								className={cn(
									"group w-52 shrink-0 rounded-sm border flex flex-col",
									colors ? `${colors.borderColor} ${colors.bgColor}` : "border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50",
									column.hidden && "opacity-50",
									isDropTarget && "border-l-4 border-l-blue-500"
								)}
								draggable
								onDragStart={(e) => handleDragStart(e, index)}
								onDragOver={(e) => handleDragOver(e, index)}
								onDrop={(e) => handleDrop(e, index)}
								onDragEnd={handleDragEnd}
							>
								<div className={cn(
									"flex items-center gap-1.5 rounded-t-sm px-2 py-2 cursor-grab active:cursor-grabbing",
									colors ? `${colors.headerBg} ${colors.headerText}` : "bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200"
								)}>
									<GripVertical size={14} className="opacity-40 group-hover:opacity-80 shrink-0" />
									<span className={`h-2.5 w-2.5 rounded-full shrink-0 ${colors ? colors.dotColor : "bg-gray-500"}`} />
									{column.isSystem ? (
										<span className="text-sm font-medium truncate flex-1">{labelFor(column)}</span>
									) : (
										<Input
											className="h-7 text-sm bg-white/70 dark:bg-gray-900/40 flex-1 min-w-0"
											value={column.label ?? ""}
											onChange={(e) => updateColumn(column.id, { label: e.target.value })}
											draggable
											onDragStart={(e) => {
												e.preventDefault();
												e.stopPropagation();
											}}
										/>
									)}
								</div>

								<div className="flex flex-col gap-2 p-2">
									{colorSelect(column)}
									<div className="flex items-center justify-between">
										{column.isSystem ? (
											<Badge variant="secondary" className="gap-1">
												<Lock className="h-3 w-3" />
												{t("admin.kanbanColumns.systemBadge")}
											</Badge>
										) : (
											<Badge variant="outline">{t("admin.kanbanColumns.customBadge")}</Badge>
										)}
										<div className="flex gap-1">
											<Button
												variant="ghost"
												size="icon"
												className="h-7 w-7"
												disabled={!column.hidden && visibleCount <= 1}
												title={column.hidden ? t("admin.kanbanColumns.show") : t("admin.kanbanColumns.hide")}
												onClick={() => updateColumn(column.id, { hidden: !column.hidden || undefined })}
											>
												{column.hidden ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
											</Button>
											{!column.isSystem && (
												<Button
													variant="ghost"
													size="icon"
													className="h-7 w-7 text-destructive"
													title={t("admin.kanbanColumns.delete")}
													onClick={() => requestDelete(column)}
												>
													<Trash2 className="h-4 w-4" />
												</Button>
											)}
										</div>
									</div>
								</div>
							</div>
						);
					})}
				</div>

				<div className="flex justify-end mt-4">
					<Button onClick={handleSave} disabled={!hasUnsavedChanges || saving}>
						{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
						{t("admin.kanbanColumns.save")}
					</Button>
				</div>
			</Card>

			<AlertDialog
				open={deleteTarget !== null && deleteTarget.matchCount === 0}
				onOpenChange={(open) => {
					if (!open) setDeleteTarget(null);
				}}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>{t("admin.kanbanColumns.deleteConfirmTitle")}</AlertDialogTitle>
						<AlertDialogDescription>
							{t("admin.kanbanColumns.deleteConfirmDesc", { label: deleteTarget ? labelFor(deleteTarget.column) : "" })}
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>{t("admin.kanbanColumns.cancel")}</AlertDialogCancel>
						<AlertDialogAction onClick={confirmDelete}>{t("admin.kanbanColumns.delete")}</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>

			<Dialog
				open={deleteTarget !== null && deleteTarget.matchCount > 0}
				onOpenChange={(open) => {
					if (!open) setDeleteTarget(null);
				}}
			>
				<DialogContent className="sm:max-w-[480px]">
					<DialogHeader>
						<DialogTitle>{t("admin.kanbanColumns.moveMatchesTitle")}</DialogTitle>
						<DialogDescription>
							{t("admin.kanbanColumns.moveMatchesDesc", {
								count: deleteTarget?.matchCount ?? 0,
								label: deleteTarget ? labelFor(deleteTarget.column) : ""
							})}
						</DialogDescription>
					</DialogHeader>
					<div className="space-y-2 py-2">
						<label className="text-sm font-medium">{t("admin.kanbanColumns.targetColumn")}</label>
						<Select value={reassignTo} onValueChange={setReassignTo}>
							<SelectTrigger>
								<SelectValue placeholder={t("admin.kanbanColumns.targetPlaceholder")} />
							</SelectTrigger>
							<SelectContent>
								{reassignOptions.map((option) => (
									<SelectItem key={option.value} value={option.value}>
										{option.label}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>
					<DialogFooter>
						<Button variant="outline" onClick={() => setDeleteTarget(null)}>
							{t("admin.kanbanColumns.cancel")}
						</Button>
						<Button onClick={confirmDelete} disabled={!reassignTo || deleting}>
							{deleting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
							{t("admin.kanbanColumns.moveAndDelete")}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</div>
	);
}
